import { UserStore } from "./user.store";
import { EVENTS_POLISH_FLAG, HOME_TEAM_FLAG } from "@/lib/launchdarkly/flags";
import { setHomeScopeProvider } from "@/services/api.service";
import { WorkspaceStore } from "./workspace.store";
import { ProjectStore } from "./project.store";
import { FavoriteStore } from "./favorite.store";
import { InstanceStore } from "./instance.store";
import { RouterStore } from "./router.store";
import { InvitationStore } from "./invitation.store";
import { IssueStore } from "./issue.store";
import { IssueMetadataStore } from "./issue-metadata.store";
import { IssueViewPreferencesStore } from "./issue-view-preferences.store";
import { IssueDetailStore } from "./issue-detail.store";
import { EventStore } from "./event.store";
import { EventDraftStore } from "./event-draft.store";
import { WorkItemViewPreferencesStore } from "./work-item-view-preferences.store";
import { ProjectsListViewPreferencesStore } from "./projects-list-view-preferences.store";
import { ProgramsListViewPreferencesStore } from "./programs-list-view-preferences.store";
import { NotificationStore } from "./notification.store";
import { listenForCacheHints } from "./cache-hints";
import { ViewStore } from "./view.store";
import { CommandPaletteStore } from "./command-palette.store";
import { TimelineStore } from "./timeline.store";
import { TimelineViewStore } from "./timeline-view.store";
import { TimelinePreferencesStore } from "./timeline-preferences.store";
import { WorkspaceSettingsStore } from "./workspace-settings.store";
import { ProfileSettingsStore } from "./profile-settings.store";
import { WorkspaceMembersStore } from "./workspace-members.store";
import { WorkspaceRolesStore } from "./workspace-roles.store";
import { WorkspaceJobRolesStore } from "./workspace-job-roles.store";
import { WorkspaceExportsStore } from "./workspace-exports.store";
import { AuditEventsStore } from "./audit-events.store";
import { WorkspaceWebhooksStore } from "./workspace-webhooks.store";
import { WorkspaceAIProviderStore } from "./workspace-ai-provider.store";
import { WorkspaceLmsConnectionStore } from "./workspace-lms-connection.store";
import { WorkspaceCapabilityStore } from "./workspace-capability.store";
import { WorkspaceTeamsConnectionStore } from "./workspace-teams-connection.store";
import { UserTeamsLinkStore } from "./user-teams-link.store";
import { ProjectLmsLinkStore } from "./project-lms-link.store";
import { SubmissionLmsRecommendationStore } from "./submission-lms-recommendation.store";
import { ProjectLmsResultsStore } from "./project-lms-results.store";
import { WorkspaceSsoStore } from "./workspace-sso.store";
import { WorkspacePasswordPolicyStore } from "./workspace-password-policy.store";
import { WorkspaceSessionPolicyStore } from "./workspace-session-policy.store";
import { WorkspaceCalendarsStore } from "./workspace-calendars.store";
import { WorkspaceSkillsStore } from "./workspace-skills.store";
import { UserSkillsStore } from "./user-skills.store";
import { WorkspaceAvailabilityStore } from "./workspace-availability.store";
import { TimeOffStore } from "./time-off.store";
import { WorklogStore } from "./worklog.store";
import { ProjectSettingsStore } from "./project-settings.store";
import { ProjectMembersStore } from "./project-members.store";
import { MentionableUsersStore } from "./mentionable-users.store";
import { WorkItemStatesStore } from "./work-item-states.store";
import { ProjectStatesStore } from "./project-states.store";
import { RequestStatusesStore } from "./request-statuses.store";
import { RequestReasonsStore } from "./request-reasons.store";
import { WorkspaceTeamsStore } from "./workspace-teams.store";
import { WorkHoursStore } from "./work-hours.store";
import { ProjectTemplatesStore } from "./project-templates.store";
import { WorkitemTemplateAttachmentsStore } from "./workitem-template-attachments.store";
import { WorkspaceTemplatesStore } from "./workspace-templates.store";
import { ProjectAttachmentsStore } from "./project-attachments.store";
import { ProjectCapacityStore } from "./project-capacity.store";
import { CapacityPlanningStore } from "./capacity-planning.store";
import { IntakeStore } from "./intake.store";
import { SlaActivityStore, SlaStore } from "./sla.store";
import { TeamSlaStore } from "./team-sla.store";
import { RequestPrioritizationStore } from "./request-prioritization.store";
import { SubmissionActivityStore, SubmissionCommentsStore } from "./intake-submission-feed.store";
import { DashboardStore } from "./dashboard.store";
import { ReportStore } from "./report.store";
import { ReportLookupStore } from "./report-lookup.store";
import { CapacityRebalanceStore } from "./capacity-rebalance.store";
import { FeatureFlagStore } from "./feature-flag.store";
import { ProjectWorkflowStore } from "./project-workflow.store";
import { RequestWorkflowStore } from "./request-workflow.store";
import { FormWorkflowStore } from "./form-workflow.store";
import { AuthService } from "@/services/auth.service";
import { UserService } from "@/services/user.service";
import { WorkspaceService } from "@/services/workspace.service";
import { ProjectService } from "@/services/project.service";
import { PermissionService } from "@/services/permission.service";
import { ProjectCustomFieldsService } from "@/services/project-custom-fields.service";
import { WorkItemCustomFieldsService } from "@/services/work-item-custom-fields.service";
import { ProjectMemberService } from "@/services/project-member.service";
import { ProjectInvitationService } from "@/services/project-invitation.service";
import { WorkItemStateService } from "@/services/work-item-state.service";
import { ProjectStateService } from "@/services/project-state.service";
import { RequestStatusService } from "@/services/request-status.service";
import { RequestReasonService } from "@/services/request-reason.service";
import { FavoriteService } from "@/services/favorite.service";
import { MyWorkService } from "@/services/my-work.service";
import { InstanceService } from "@/services/instance.service";
import { InvitationService } from "@/services/invitation.service";
import { IssueArchiveService } from "@/services/issue-archive.service";
import { WorkItemService } from "@/services/work-item.service";
import { IssueCommentService } from "@/services/issue-comment.service";
import { MentionableUsersService } from "@/services/mentionable-users.service";
import { IssueAttachmentService } from "@/services/issue-attachment.service";
import { IssueMetadataService } from "@/services/issue-metadata.service";
import { EventService } from "@/services/event.service";
import { NotificationService } from "@/services/notification.service";
import { ViewService } from "@/services/view.service";
import { WorkspaceRoleService } from "@/services/workspace-role.service";
import { WorkspaceJobRoleService } from "@/services/workspace-job-role.service";
import { WorkspaceExportService } from "@/services/workspace-export.service";
import { WorkspaceWebhookService } from "@/services/workspace-webhook.service";
import { WorkspaceAIProviderService } from "@/services/workspace-ai-provider.service";
import { LmsConnectionService } from "@/services/lms-connection.service";
import { IntegrationCapabilityService } from "@/services/integration-capability.service";
import { MsTeamsConnectionService } from "@/services/msteams-connection.service";
import { MsTeamsLinkService } from "@/services/msteams-link.service";
import { LmsLinkService } from "@/services/lms-link.service";
import { LmsRecommendationService } from "@/services/lms-recommendation.service";
import { LmsResultsService } from "@/services/lms-results.service";
import { WorkspaceSsoService } from "@/services/workspace-sso.service";
import { WorkspacePasswordPolicyService } from "@/services/workspace-password-policy.service";
import { WorkspaceSessionPolicyService } from "@/services/workspace-session-policy.service";
import { WorkspaceCalendarService } from "@/services/workspace-calendar.service";
import { AuditService } from "@/services/audit.service";
import { WorkspaceSkillService } from "@/services/workspace-skill.service";
import { WorkspaceAvailabilityService } from "@/services/workspace-availability.service";
import { WorkspaceCapacityService } from "@/services/workspace-capacity.service";
import { TimeOffService } from "@/services/time-off.service";
import { WorkspaceTeamService } from "@/services/workspace-team.service";
import { WorkspaceAssetService } from "@/services/workspace-asset.service";
import { UserAssetService } from "@/services/user-asset.service";
import { TimezoneService } from "@/services/timezone.service";
import { CurrencyService } from "@/services/currency.service";
import { WorklogService } from "@/services/worklog.service";
import { WorkHoursService } from "@/services/work-hours.service";
import { ProjectTemplateService } from "@/services/project-template.service";
import { WorkspaceTemplateService } from "@/services/workspace-template.service";
import { ProjectCapacityService } from "@/services/project-capacity.service";
import { IntakeService } from "@/services/intake.service";
import { SlaService } from "@/services/sla.service";
import { RequestPrioritizationService } from "@/services/request-prioritization.service";
import { DashboardService } from "@/services/dashboard.service";
import { ReportService } from "@/services/report.service";
import { CapacityRebalanceService } from "@/services/capacity-rebalance.service";
import { ProjectWorkflowService } from "@/services/project-workflow.service";
import {
  GoalsService,
  ProcessesService,
  ProgramActivityService,
  ProgramCommentService,
  ProgramsService,
  ProjectActivityService,
  ProjectCommentService,
  ProjectV3Service,
} from "@/services/projects-v3.service";
import {
  GoalsStore,
  ProcessesStore,
  ProjectLinkedSubmissionsStore,
  ProgramActivityStore,
  ProgramCommentsStore,
  ProgramsStore,
  ProjectActivityStore,
  ProjectCommentsStore,
  ProjectV3Store,
} from "./projects-v3.store";
import { WorkspaceCustomFieldService } from "@/services/workspace-custom-field.service";
import { WorkspaceCategoriesService } from "@/services/workspace-categories.service";
import { WorkspaceCategoriesStore } from "./workspace-categories.store";
import { WorkspaceCustomFieldsStore } from "./workspace-custom-fields.store";
import { RequestWorkflowService } from "@/services/request-workflow.service";
import { FormWorkflowService } from "@/services/form-workflow.service";
import { TimelineService } from "@/services/timeline.service";
import {
  ReportingAgentSocket,
  type IReportingAgentSocket,
} from "@/services/reporting-agent.socket";
import { ReportingAgentStore } from "./reporting-agent.store";
import { ReportingAgentService } from "@/services/reporting-agent.service";
import { ReportingAgentDashboardsStore } from "./reporting-agent-dashboards.store";

export class RootStore {
  authService: AuthService;
  userService: UserService;
  workspaceService: WorkspaceService;
  projectService: ProjectService;
  projectCustomFieldsService: ProjectCustomFieldsService;
  workItemCustomFieldsService: WorkItemCustomFieldsService;
  projectMemberService: ProjectMemberService;
  projectInvitationService: ProjectInvitationService;
  workItemStateService: WorkItemStateService;
  projectStateService: ProjectStateService;
  requestStatusService: RequestStatusService;
  requestReasonService: RequestReasonService;
  favoriteService: FavoriteService;
  myWorkService: MyWorkService;
  instanceService: InstanceService;
  invitationService: InvitationService;
  workItemService: WorkItemService;
  issueService: WorkItemService;
  issueArchiveService: IssueArchiveService;
  issueCommentService: IssueCommentService;
  mentionableUsersService: MentionableUsersService;
  issueAttachmentService: IssueAttachmentService;
  issueMetadataService: IssueMetadataService;
  eventService: EventService;
  notificationService: NotificationService;
  viewService: ViewService;
  workspaceRoleService: WorkspaceRoleService;
  workspaceJobRoleService: WorkspaceJobRoleService;
  workspaceExportService: WorkspaceExportService;
  workspaceWebhookService: WorkspaceWebhookService;
  workspaceAIProviderService: WorkspaceAIProviderService;
  lmsConnectionService: LmsConnectionService;
  integrationCapabilityService: IntegrationCapabilityService;
  msTeamsConnectionService: MsTeamsConnectionService;
  msTeamsLinkService: MsTeamsLinkService;
  lmsLinkService: LmsLinkService;
  lmsRecommendationService: LmsRecommendationService;
  lmsResultsService: LmsResultsService;
  workspaceSsoService: WorkspaceSsoService;
  workspacePasswordPolicyService: WorkspacePasswordPolicyService;
  workspaceSessionPolicyService: WorkspaceSessionPolicyService;
  workspaceCalendarService: WorkspaceCalendarService;
  auditService: AuditService;
  workspaceSkillService: WorkspaceSkillService;
  workspaceAvailabilityService: WorkspaceAvailabilityService;
  workspaceCapacityService: WorkspaceCapacityService;
  timeOffService: TimeOffService;
  workspaceTeamService: WorkspaceTeamService;
  workspaceAssetService: WorkspaceAssetService;
  userAssetService: UserAssetService;
  timezoneService: TimezoneService;
  currencyService: CurrencyService;
  worklogService: WorklogService;
  workHoursService: WorkHoursService;
  projectTemplateService: ProjectTemplateService;
  workspaceTemplateService: WorkspaceTemplateService;
  projectCapacityService: ProjectCapacityService;
  intakeService: IntakeService;
  slaService: SlaService;
  requestPrioritizationService: RequestPrioritizationService;
  dashboardService: DashboardService;
  reportService: ReportService;
  capacityRebalanceService: CapacityRebalanceService;
  projectWorkflowService: ProjectWorkflowService;
  programsService: ProgramsService;
  goalsService: GoalsService;
  workspaceCustomFieldService: WorkspaceCustomFieldService;
  workspaceCategoriesService: WorkspaceCategoriesService;
  processesService: ProcessesService;
  permissionService: PermissionService;
  programActivityService: ProgramActivityService;
  programCommentService: ProgramCommentService;
  projectActivityService: ProjectActivityService;
  projectCommentService: ProjectCommentService;
  projectV3Service: ProjectV3Service;
  requestWorkflowService: RequestWorkflowService;
  formWorkflowService: FormWorkflowService;
  timelineService: TimelineService;
  reportingAgentSocket: IReportingAgentSocket;
  reportingAgentService: ReportingAgentService;
  reportingAgentDashboards: ReportingAgentDashboardsStore;

  user: UserStore;
  workspace: WorkspaceStore;
  profileSettings: ProfileSettingsStore;
  workspaceSettings: WorkspaceSettingsStore;
  workspaceMembers: WorkspaceMembersStore;
  workspaceRoles: WorkspaceRolesStore;
  workspaceJobRoles: WorkspaceJobRolesStore;
  workspaceExports: WorkspaceExportsStore;
  auditEvents: AuditEventsStore;
  workspaceWebhooks: WorkspaceWebhooksStore;
  workspaceAIProvider: WorkspaceAIProviderStore;
  lmsConnections: WorkspaceLmsConnectionStore;
  workspaceCapabilities: WorkspaceCapabilityStore;
  teamsConnection: WorkspaceTeamsConnectionStore;
  userTeamsLink: UserTeamsLinkStore;
  projectLmsLinks: ProjectLmsLinkStore;
  submissionLmsRecommendations: SubmissionLmsRecommendationStore;
  projectLmsResults: ProjectLmsResultsStore;
  workspaceSso: WorkspaceSsoStore;
  workspacePasswordPolicy: WorkspacePasswordPolicyStore;
  workspaceSessionPolicy: WorkspaceSessionPolicyStore;
  workspaceCalendars: WorkspaceCalendarsStore;
  workspaceSkills: WorkspaceSkillsStore;
  userSkills: UserSkillsStore;
  workspaceAvailability: WorkspaceAvailabilityStore;
  timeOff: TimeOffStore;
  worklog: WorklogStore;
  workspaceTeams: WorkspaceTeamsStore;
  workHours: WorkHoursStore;
  projectTemplates: ProjectTemplatesStore;
  workitemTemplateAttachments: WorkitemTemplateAttachmentsStore;
  workspaceTemplates: WorkspaceTemplatesStore;
  projectCapacity: ProjectCapacityStore;
  capacityPlanning: CapacityPlanningStore;
  intakeStore: IntakeStore;
  sla: SlaStore;
  slaActivity: SlaActivityStore;
  teamSla: TeamSlaStore;
  requestPrioritization: RequestPrioritizationStore;
  submissionActivity: SubmissionActivityStore;
  submissionComments: SubmissionCommentsStore;
  dashboard: DashboardStore;
  report: ReportStore;
  reportLookups: ReportLookupStore;
  project: ProjectStore;
  projectSettings: ProjectSettingsStore;
  projectMembers: ProjectMembersStore;
  mentionableUsers: MentionableUsersStore;
  workItemStates: WorkItemStatesStore;
  projectStates: ProjectStatesStore;
  requestStatuses: RequestStatusesStore;
  requestReasons: RequestReasonsStore;
  projectWorkflow: ProjectWorkflowStore;
  requestWorkflow: RequestWorkflowStore;
  formWorkflow: FormWorkflowStore;
  favorite: FavoriteStore;
  instance: InstanceStore;
  router: RouterStore;
  invitation: InvitationStore;
  issue: IssueStore;
  issueMetadata: IssueMetadataStore;
  issueViewPreferences: IssueViewPreferencesStore;
  issueDetail: IssueDetailStore;
  event: EventStore;
  eventDraft: EventDraftStore;
  workItemViewPreferences: WorkItemViewPreferencesStore;
  projectsListViewPreferences: ProjectsListViewPreferencesStore;
  programsListViewPreferences: ProgramsListViewPreferencesStore;
  notification: NotificationStore;
  view: ViewStore;
  commandPalette: CommandPaletteStore;
  timeline: TimelineStore;
  timelineView: TimelineViewStore;
  timelinePreferences: TimelinePreferencesStore;
  capacityRebalance: CapacityRebalanceStore;
  featureFlags: FeatureFlagStore;
  programs: ProgramsStore;
  goals: GoalsStore;
  workspaceCustomFields: WorkspaceCustomFieldsStore;
  workspaceCategories: WorkspaceCategoriesStore;
  processes: ProcessesStore;
  programActivity: ProgramActivityStore;
  programComments: ProgramCommentsStore;
  projectActivity: ProjectActivityStore;
  projectAttachments: ProjectAttachmentsStore;
  projectComments: ProjectCommentsStore;
  projectLinkedSubmissions: ProjectLinkedSubmissionsStore;
  projectV3: ProjectV3Store;
  reportingAgent: ReportingAgentStore;

  constructor() {
    this.authService = new AuthService();
    this.userService = new UserService();
    this.workspaceService = new WorkspaceService();
    this.projectService = new ProjectService();
    this.projectCustomFieldsService = new ProjectCustomFieldsService();
    this.workItemCustomFieldsService = new WorkItemCustomFieldsService();
    this.projectMemberService = new ProjectMemberService();
    this.projectInvitationService = new ProjectInvitationService();
    this.workItemStateService = new WorkItemStateService();
    this.projectStateService = new ProjectStateService();
    this.requestStatusService = new RequestStatusService();
    this.requestReasonService = new RequestReasonService();
    this.favoriteService = new FavoriteService();
    this.myWorkService = new MyWorkService();
    this.instanceService = new InstanceService();
    this.invitationService = new InvitationService();
    this.workItemService = new WorkItemService(() =>
      this.featureFlags.isEnabled(EVENTS_POLISH_FLAG),
    );
    this.issueService = this.workItemService;
    this.issueArchiveService = new IssueArchiveService(() =>
      this.featureFlags.isEnabled(EVENTS_POLISH_FLAG),
    );
    this.issueCommentService = new IssueCommentService();
    this.mentionableUsersService = new MentionableUsersService();
    this.issueAttachmentService = new IssueAttachmentService();
    this.issueMetadataService = new IssueMetadataService();
    this.eventService = new EventService();
    this.notificationService = new NotificationService();
    this.viewService = new ViewService();
    this.workspaceRoleService = new WorkspaceRoleService();
    this.workspaceJobRoleService = new WorkspaceJobRoleService();
    this.workspaceExportService = new WorkspaceExportService();
    this.workspaceWebhookService = new WorkspaceWebhookService();
    this.workspaceAIProviderService = new WorkspaceAIProviderService();
    this.lmsConnectionService = new LmsConnectionService();
    this.integrationCapabilityService = new IntegrationCapabilityService();
    this.msTeamsConnectionService = new MsTeamsConnectionService();
    this.msTeamsLinkService = new MsTeamsLinkService();
    this.lmsLinkService = new LmsLinkService();
    this.lmsRecommendationService = new LmsRecommendationService();
    this.lmsResultsService = new LmsResultsService();
    this.workspaceSsoService = new WorkspaceSsoService();
    this.workspacePasswordPolicyService = new WorkspacePasswordPolicyService();
    this.workspaceSessionPolicyService = new WorkspaceSessionPolicyService();
    this.workspaceCalendarService = new WorkspaceCalendarService();
    this.auditService = new AuditService();
    this.workspaceSkillService = new WorkspaceSkillService();
    this.workspaceAvailabilityService = new WorkspaceAvailabilityService();
    this.workspaceCapacityService = new WorkspaceCapacityService();
    this.timeOffService = new TimeOffService();
    this.workspaceTeamService = new WorkspaceTeamService();
    this.workspaceAssetService = new WorkspaceAssetService();
    this.userAssetService = new UserAssetService();
    this.timezoneService = new TimezoneService();
    this.currencyService = new CurrencyService();
    this.worklogService = new WorklogService();
    this.workHoursService = new WorkHoursService();
    this.projectTemplateService = new ProjectTemplateService();
    this.workspaceTemplateService = new WorkspaceTemplateService();
    this.projectCapacityService = new ProjectCapacityService();
    this.intakeService = new IntakeService();
    this.slaService = new SlaService();
    this.requestPrioritizationService = new RequestPrioritizationService();
    this.dashboardService = new DashboardService();
    this.reportService = new ReportService();
    this.capacityRebalanceService = new CapacityRebalanceService();
    this.projectWorkflowService = new ProjectWorkflowService();
    this.programsService = new ProgramsService();
    this.goalsService = new GoalsService();
    this.workspaceCustomFieldService = new WorkspaceCustomFieldService();
    this.workspaceCategoriesService = new WorkspaceCategoriesService();
    this.processesService = new ProcessesService();
    this.permissionService = new PermissionService();
    this.programActivityService = new ProgramActivityService();
    this.programCommentService = new ProgramCommentService();
    this.projectActivityService = new ProjectActivityService();
    this.projectCommentService = new ProjectCommentService();
    this.projectV3Service = new ProjectV3Service();
    this.requestWorkflowService = new RequestWorkflowService();
    this.formWorkflowService = new FormWorkflowService();
    this.timelineService = new TimelineService();
    this.reportingAgentSocket = new ReportingAgentSocket();
    this.reportingAgentService = new ReportingAgentService();

    this.user = new UserStore(this);
    this.workspace = new WorkspaceStore(this);
    this.profileSettings = new ProfileSettingsStore(this);
    this.workspaceSettings = new WorkspaceSettingsStore(this);
    this.workspaceMembers = new WorkspaceMembersStore(this);
    this.workspaceRoles = new WorkspaceRolesStore(this);
    this.workspaceJobRoles = new WorkspaceJobRolesStore(this);
    this.workspaceExports = new WorkspaceExportsStore(this);
    this.auditEvents = new AuditEventsStore(this);
    this.workspaceWebhooks = new WorkspaceWebhooksStore(this);
    this.workspaceAIProvider = new WorkspaceAIProviderStore(this);
    this.lmsConnections = new WorkspaceLmsConnectionStore(this);
    this.workspaceCapabilities = new WorkspaceCapabilityStore(this.integrationCapabilityService);
    this.teamsConnection = new WorkspaceTeamsConnectionStore(this);
    this.userTeamsLink = new UserTeamsLinkStore(this);
    this.projectLmsLinks = new ProjectLmsLinkStore(this);
    this.submissionLmsRecommendations = new SubmissionLmsRecommendationStore(this);
    this.projectLmsResults = new ProjectLmsResultsStore(this);
    this.workspaceSso = new WorkspaceSsoStore(this);
    this.workspacePasswordPolicy = new WorkspacePasswordPolicyStore(this);
    this.workspaceSessionPolicy = new WorkspaceSessionPolicyStore(this);
    this.workspaceCalendars = new WorkspaceCalendarsStore(this);
    this.workspaceSkills = new WorkspaceSkillsStore(this);
    this.userSkills = new UserSkillsStore(this);
    this.workspaceAvailability = new WorkspaceAvailabilityStore(this);
    this.timeOff = new TimeOffStore(this);
    this.worklog = new WorklogStore(this);
    this.workspaceTeams = new WorkspaceTeamsStore(this);
    this.workHours = new WorkHoursStore(this);
    this.projectTemplates = new ProjectTemplatesStore(this);
    this.workitemTemplateAttachments = new WorkitemTemplateAttachmentsStore(this);
    this.workspaceTemplates = new WorkspaceTemplatesStore(this);
    this.projectCapacity = new ProjectCapacityStore(this);
    this.capacityPlanning = new CapacityPlanningStore(this);
    this.intakeStore = new IntakeStore(this);
    this.sla = new SlaStore(this);
    this.slaActivity = new SlaActivityStore(this);
    this.teamSla = new TeamSlaStore({
      slaService: this.slaService,
      intakeService: this.intakeService,
      workspaceTeamService: this.workspaceTeamService,
    });
    this.requestPrioritization = new RequestPrioritizationStore(this);
    this.submissionActivity = new SubmissionActivityStore(this);
    this.submissionComments = new SubmissionCommentsStore(this);
    this.dashboard = new DashboardStore(this);
    this.report = new ReportStore(this);
    this.reportLookups = new ReportLookupStore(this);
    this.project = new ProjectStore(this);
    this.projectSettings = new ProjectSettingsStore(this);
    this.projectMembers = new ProjectMembersStore(this);
    this.mentionableUsers = new MentionableUsersStore(this);
    this.workItemStates = new WorkItemStatesStore(this);
    this.projectStates = new ProjectStatesStore(this);
    this.requestStatuses = new RequestStatusesStore(this);
    this.requestReasons = new RequestReasonsStore(this);
    this.projectWorkflow = new ProjectWorkflowStore(this);
    this.requestWorkflow = new RequestWorkflowStore(this);
    this.formWorkflow = new FormWorkflowStore(this);
    this.favorite = new FavoriteStore(this);
    this.instance = new InstanceStore(this);
    this.router = new RouterStore();
    this.invitation = new InvitationStore(this);
    this.issue = new IssueStore(this);
    this.issueMetadata = new IssueMetadataStore(this);
    this.issueViewPreferences = new IssueViewPreferencesStore(this);
    this.issueDetail = new IssueDetailStore(this);
    this.event = new EventStore(this);
    this.eventDraft = new EventDraftStore();
    this.workItemViewPreferences = new WorkItemViewPreferencesStore();
    this.projectsListViewPreferences = new ProjectsListViewPreferencesStore();
    this.programsListViewPreferences = new ProgramsListViewPreferencesStore();
    this.notification = new NotificationStore(this);
    this.view = new ViewStore(this);
    this.commandPalette = new CommandPaletteStore();
    this.timeline = new TimelineStore(this);
    this.timelineView = new TimelineViewStore(this);
    this.timelinePreferences = new TimelinePreferencesStore();
    this.capacityRebalance = new CapacityRebalanceStore({
      capacityRebalanceService: this.capacityRebalanceService,
      workItemService: this.workItemService,
    });
    this.featureFlags = new FeatureFlagStore(this);
    // Every API request asks for the Home team downward read rule while
    // `home-team` is on (NG-2174). Read per request, so a flag change applies to
    // the next request, and through `this` so a reset store's flags are the ones read.
    setHomeScopeProvider(() => this.featureFlags?.isEnabled(HOME_TEAM_FLAG) ?? false);
    this.programs = new ProgramsStore(this, this.programsService);
    this.goals = new GoalsStore(this, this.goalsService);
    this.workspaceCustomFields = new WorkspaceCustomFieldsStore(this);
    this.workspaceCategories = new WorkspaceCategoriesStore(this);
    this.processes = new ProcessesStore(this, this.processesService);
    this.programActivity = new ProgramActivityStore(this, this.programActivityService);
    this.programComments = new ProgramCommentsStore(this, this.programCommentService);
    this.projectActivity = new ProjectActivityStore(this, this.projectActivityService);
    this.projectAttachments = new ProjectAttachmentsStore(this.projectService, this);
    this.projectComments = new ProjectCommentsStore(this, this.projectCommentService);
    this.projectLinkedSubmissions = new ProjectLinkedSubmissionsStore(this);
    this.projectV3 = new ProjectV3Store(this, this.projectV3Service);
    this.reportingAgent = new ReportingAgentStore(this);
    this.reportingAgentDashboards = new ReportingAgentDashboardsStore(this);
    // Cache-invalidation hints share the notification stream (NG-2666). The
    // root store lives as long as the tab, so the listener is never removed.
    listenForCacheHints(this);
  }
}
